import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import type { BreakpointSpec, TemplateZone } from '../types/creative-template.types';
import { CANVAS_CLASS_OPTIONS } from '../types/creative-template.types';
import { EMPTY_BREAKPOINT, EMPTY_ZONE } from '../helpers/creative-template.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';
import { ZonePreview } from './zone-preview';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

interface BreakpointSpecsEditorProps {
  values: BreakpointSpec[];
  onChange: (values: BreakpointSpec[]) => void;
}

function ZoneFields({
  role,
  zone,
  onChange,
  onRemove,
}: {
  role: string;
  zone: TemplateZone;
  onChange: (zone: TemplateZone) => void;
  onRemove: () => void;
}) {
  const fields: Array<keyof TemplateZone> = ['x', 'y', 'w', 'h'];
  return (
    <div className="grid gap-2 rounded border border-gray-200 bg-white p-2 md:grid-cols-5">
      <div className="flex items-center text-sm font-medium text-black">{role}</div>
      {fields.map((field) => (
        <label key={field} className="space-y-1">
          <span className="text-xs text-gray-500">{field}</span>
          <Input
            type="number"
            step="0.01"
            min={0}
            max={1}
            value={zone[field]}
            onChange={(event) => onChange({ ...zone, [field]: Number(event.target.value) })}
          />
        </label>
      ))}
      <div className="flex items-end">
        <SchedulerDsButton type="button" variant="danger-ghost" size="sm" onClick={onRemove}>
          <Trash2 className="size-4" />
        </SchedulerDsButton>
      </div>
    </div>
  );
}

export function BreakpointSpecsEditor({ values, onChange }: BreakpointSpecsEditorProps) {
  const updateBreakpoint = (index: number, next: BreakpointSpec) => {
    onChange(values.map((value, itemIndex) => (itemIndex === index ? next : value)));
  };

  const addZone = (index: number, role: string) => {
    const breakpoint = values[index];
    updateBreakpoint(index, {
      ...breakpoint,
      zones: { ...breakpoint.zones, [role]: { ...EMPTY_ZONE } },
    });
  };

  const removeZone = (index: number, role: string) => {
    const breakpoint = values[index];
    const { [role]: _removed, ...zones } = breakpoint.zones;
    updateBreakpoint(index, { ...breakpoint, zones });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Breakpoints</h3>
        <SchedulerDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...values, { ...EMPTY_BREAKPOINT, zones: {} }])}
        >
          <Plus className="size-4" />
          Add breakpoint
        </SchedulerDsButton>
      </div>

      {values.map((breakpoint, index) => (
        <div key={`breakpoint-${index}`} className="space-y-4 rounded-md border border-gray-300 bg-gray-50 p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <label className="relative block space-y-1">
              <span className={labelClassName}>Canvas class</span>
              <select
                value={breakpoint.canvas_class}
                onChange={(event) => updateBreakpoint(index, {
                  ...breakpoint,
                  canvas_class: event.target.value as BreakpointSpec['canvas_class'],
                })}
                className={selectClassName}
              >
                {CANVAS_CLASS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute bottom-3 right-3 size-4 text-gray-500" />
            </label>
            <label className="space-y-1">
              <span className={labelClassName}>Max text roles</span>
              <Input
                type="number"
                value={breakpoint.max_text_roles}
                onChange={(event) => updateBreakpoint(index, {
                  ...breakpoint,
                  max_text_roles: Number(event.target.value),
                })}
              />
            </label>
            <label className="space-y-1">
              <span className={labelClassName}>Min hero area frac</span>
              <Input
                type="number"
                step="0.01"
                value={breakpoint.min_hero_area_frac}
                onChange={(event) => updateBreakpoint(index, {
                  ...breakpoint,
                  min_hero_area_frac: Number(event.target.value),
                })}
              />
            </label>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={labelClassName}>Zones</span>
                <SchedulerDsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const role = window.prompt('Role name (e.g. headline, hero):')?.trim();
                    if (role) {
                      addZone(index, role);
                    }
                  }}
                >
                  <Plus className="size-4" />
                  Add zone
                </SchedulerDsButton>
              </div>
              {Object.entries(breakpoint.zones).map(([role, zone]) => (
                <ZoneFields
                  key={role}
                  role={role}
                  zone={zone}
                  onChange={(next) => updateBreakpoint(index, {
                    ...breakpoint,
                    zones: { ...breakpoint.zones, [role]: next },
                  })}
                  onRemove={() => removeZone(index, role)}
                />
              ))}
            </div>
            <ZonePreview canvasClass={breakpoint.canvas_class} zones={breakpoint.zones} />
          </div>

          <SchedulerDsButton
            type="button"
            variant="danger-ghost"
            size="sm"
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2 className="size-4" />
            Remove breakpoint
          </SchedulerDsButton>
        </div>
      ))}
    </section>
  );
}
